import { IEventProp, trackEvent } from "../../../helpers/analytics";
import { analytics } from "../../../templates/Layout/Segment/segmentLoader";
import { AdditionalBorrowing } from "../../../types/common";
import { FormData } from "./types";

function NumberOrUndef(input: string | undefined) {
  if (!input) {
    return undefined;
  }

  const num = Number(input.replace(/,/g, ""));

  if (Number.isNaN(num)) {
    return undefined;
  }

  return num;
}

function isAdditionalBorrowing(input: string | undefined) {
  if (!input) {
    return undefined;
  }

  return input === AdditionalBorrowing.Yes;
}

export enum FF2EventName {
  VIEW = "view",
  MORTGAGE_TOO_SMALL = "mortgage-too-small",
  REDIRECT_MIP = "redirect-mip",
  REDIRECT_MORTGAGE_COMPARISON = "redirect-mortgage-comparison",
  REDIRECT_REMO_HOLDING_PAGE = "redirect-remo-holding-page",
  REDIRECT_SIGNUP_MIP = "redirect-signup-mip",
  REDIRECT_SIGNUP_PURCHASE = "redirect-signup-purchase",
  REDIRECT_SIGNUP_REMO = "redirect-signup-remo",
  REDIRECT_SECURED_LOANS = "redirect-secured-loans",
  MAGIC_LINK_SENT = "Sent magic link"
}

export async function trackFF2Event(eventName: FF2EventName, formData: FormData) {
  const eventPayload: IEventProp = {
    module: "FF2",
    total_deposit: NumberOrUndef(formData.deposit),
    property_price: NumberOrUndef(formData.propertyValue),
    additional_borrowing: isAdditionalBorrowing(formData.additionalBorrowing),
    balance: NumberOrUndef(formData.balance),
    borrowing_amount: NumberOrUndef(formData.borrowAmount),
    mortgage_purpose: formData.mortgagePurpose,
    home_buying_stage: formData.homeBuyingStage,
    deal_expiry: formData.dealExpiry,
    combined_income: NumberOrUndef(formData.combinedIncome),
    next_steps: formData.nextSteps
  };

  return trackEvent(eventName, eventPayload);
}

const hash = async (string: string) => {
  const utf8 = new TextEncoder().encode(string);
  const hashBuffer = await crypto.subtle.digest("SHA-256", utf8);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map((bytes) => bytes.toString(16).padStart(2, "0")).join("");
  return hashHex;
};

export enum CustomerType {
  MIP = "MIP",
  PURCHASE = "PURCHASE",
  REMORTGAGE = "REMORTGAGE"
}

interface IIdentifyEventParams {
  email?: string;
  firstName?: string;
  applicantId?: string;
  rsCode?: string | null;
}

// Copied mostly as-is from Factfind
const identifyEvent = ({
  email,
  firstName,
  applicantId,
  rsCode
}: IIdentifyEventParams) =>
  new Promise((resolve) => {
    if (typeof analytics === "undefined") {
      resolve(undefined);
      return;
    }

    const identifyTimeout = setTimeout(() => {
      resolve(undefined);
    }, 1000);

    const callback = () => {
      clearTimeout(identifyTimeout);
      resolve(undefined);
    };

    analytics.identify(
      applicantId,
      {
        email,
        firstName,
        referredByCodes: rsCode ? [rsCode] : undefined
      },
      callback
    );
  });

// Copied mostly as-is from Factfind
const aliasEvent = (email: string) => new Promise((resolve) => {
  if (typeof analytics === "undefined") {
    resolve(undefined);
    return;
  }

  const aliasTimeout = setTimeout(() => {
    resolve(undefined);
  }, 1000);

  const callback = () => {
    clearTimeout(aliasTimeout);
    resolve(undefined);
  };

  analytics.alias(email, callback);
});

export const trackSignup = async (
  signUpType: CustomerType,
  email: string,
  firstName: string,
  telephone: string,
  mainApplicantId?: string,
  rsCode?: string | null,
  additionalOptions: { [key: string]: any } = {}
) => {
  const secure = crypto.subtle !== undefined;
  const enhancedTrackingParams = secure
    ? {
      sha256_email_address: await hash(email),
      address: {
        sha256_first_name: await hash(firstName)
      },
      sha256_phone_number: await hash(`+44${parseInt(telephone.toString() || "", 10)}`)
    } : {};

  await trackEvent("Created account", {
    "Signup Type": signUpType,
    ...enhancedTrackingParams,
    ...additionalOptions
  });

  await identifyEvent({
    email, firstName, applicantId: mainApplicantId, rsCode
  });

  await aliasEvent(email);
};
